import { isProtectedField } from '../protected-fields';
import { fieldLabel, recordIdentity } from './catalog';
import type { EntityContract, FieldSpec, RecordData, Value } from './types';

export function initialValues(
  fields: FieldSpec[],
  record: RecordData = {}
): Record<string, string> {
  return Object.fromEntries(
    fields.map((field) => [field.name, String(record[field.name] ?? field.defaultValue ?? '')])
  );
}
export function parseFields(fields: FieldSpec[], values: Record<string, string>, editing = false) {
  const data: RecordData = {};
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const raw = (values[field.name] ?? '').trim();
    const label = fieldLabel(field.name);
    if (!raw) {
      if (field.required) errors[field.name] = `${label} is required.`;
      else if (editing && field.nullable) data[field.name] = null;
      continue;
    }
    if (field.kind === 'number') {
      const number = Number(raw);
      if (!Number.isFinite(number) || (field.integer && !Number.isInteger(number)))
        errors[field.name] = `Enter a valid ${field.integer ? 'whole ' : ''}number.`;
      else if (
        field.min !== undefined &&
        (field.exclusiveMin ? number <= field.min : number < field.min)
      )
        errors[field.name] =
          `Must be ${field.exclusiveMin ? 'greater than' : 'at least'} ${field.min}.`;
      else if (field.max !== undefined && number > field.max)
        errors[field.name] = `Must be no more than ${field.max}.`;
      data[field.name] = number;
    } else if (field.kind === 'boolean') {
      if (!['true', 'false'].includes(raw)) errors[field.name] = 'Choose Yes or No.';
      else data[field.name] = raw === 'true';
    } else {
      const text = field.uppercase ? raw.toUpperCase() : raw;
      if (field.options && !field.options.includes(text))
        errors[field.name] = 'Choose an available option.';
      if (field.min !== undefined && text.length < field.min)
        errors[field.name] = `${label} is required.`;
      if (field.max !== undefined && text.length > field.max)
        errors[field.name] = `Use at most ${field.max} characters.`;
      if (field.pattern && !new RegExp(field.pattern).test(text))
        errors[field.name] = 'Check the format of this value.';
      if (
        field.kind === 'date' &&
        (!/^\d{4}-\d{2}-\d{2}$/.test(text) ||
          !Number.isFinite(Date.parse(text)) ||
          new Date(text).toISOString().slice(0, 10) !== text)
      )
        errors[field.name] = 'Enter a valid calendar date.';
      if (field.kind === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text))
        errors[field.name] = 'Enter a valid email address.';
      if (
        field.kind === 'reference' &&
        !/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(text)
      )
        errors[field.name] = 'Choose a registered record.';
      data[field.name] = text;
    }
  }
  return { data, errors };
}
export function businessErrors(id: string, data: RecordData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (id === 'payment-terms') {
    const hasBase = data.base_date != null && data.base_date !== '';
    const hasDays = data.due_days != null && data.due_days !== '';
    if (hasBase !== hasDays) {
      errors.base_date = 'Choose a base date and number of days together, or clear both.';
      errors.due_days = errors.base_date;
    }
  }
  if (
    id === 'tax-exemptions' &&
    data.tax_exempt &&
    data.exemption_basis !== 'export' &&
    !data.exemption_certificate_reference
  )
    errors.exemption_certificate_reference =
      'A certificate reference is required for an exemption.';
  if (id === 'tax-exemptions' && data.tax_exempt === false && data.exemption_basis)
    errors.exemption_basis = 'An exemption basis is only allowed when tax exempt is Yes.';
  if (id === 'product-information-records' && data.record_type === 'SOURCE' && data.revision_flag)
    errors.revision_flag = 'Revision markings belong to working records only.';
  if (id === 'product-information-records' && data.revision_flag && !data.revision_reason)
    errors.revision_reason = 'Explain the revision.';
  if (
    id === 'document-signing-authorities' &&
    Boolean(data.permitted_role) === Boolean(data.signer_employee_id)
  )
    errors.signer_employee_id = 'Choose either a permitted role or an employee, not both.';
  if (id === 'exchange-rates' && data.from_currency_code === data.to_currency_code)
    errors.to_currency_code = 'Choose a different currency.';
  if (id === 'product-prices' && data.approved_by && data.entered_by === data.approved_by)
    errors.approved_by = 'The reviewer must be a different employee from the author.';
  if (
    id === 'product-prices' &&
    data.price_type === 'SELL' &&
    (data.supplier_code || data.factory_id)
  )
    errors.supplier_code = 'Supplier and factory are only allowed for BUY prices.';
  if (id === 'product-prices' && data.factory_id && !data.supplier_code)
    errors.supplier_code = 'Choose a supplier before a factory.';
  if (
    id === 'inventory-planning-settings' &&
    typeof data.inventory_floor_days === 'number' &&
    typeof data.maximum_inventory_days === 'number' &&
    data.inventory_floor_days > data.maximum_inventory_days
  )
    errors.maximum_inventory_days = 'The ceiling must be at least the floor.';
  return errors;
}
export function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}
export function recordState(row: RecordData): string {
  if (row.approval_status && row.approval_status !== 'approved') return String(row.approval_status);
  if (row.is_active === false) return 'Inactive';
  if (typeof row.effective_from === 'string') {
    if (row.effective_from > utcToday()) return 'Scheduled';
    if (typeof row.effective_to === 'string' && row.effective_to < utcToday()) return 'Historical';
    return 'Current';
  }
  if (row.record_type === 'SOURCE') return 'Source · read only';
  return row.is_active === true ? 'Active' : 'Registered';
}
export function editableFields(entity: EntityContract, row: RecordData): FieldSpec[] {
  if (entity.id === 'business-settings' && row.deprecated === true) return [];
  if (
    entity.id === 'business-settings' &&
    typeof row.effective_from === 'string' &&
    row.effective_from.slice(0, 10) < utcToday()
  )
    return entity.update.filter((field) => field.name === 'description');
  if (entity.id === 'product-tariff-classifications')
    return typeof row.effective_from === 'string' && row.effective_from.slice(0, 10) > utcToday()
      ? entity.update
      : [];
  if (entity.id === 'product-information-records' && row.record_type === 'SOURCE') return [];
  if (entity.id === 'product-prices') return row.approval_status === 'pending' ? entity.update : [];
  const outsidePeriod =
    typeof row.effective_from === 'string' &&
    (row.effective_from > utcToday() ||
      (typeof row.effective_to === 'string' && row.effective_to < utcToday()));
  return outsidePeriod
    ? entity.update.filter((field) => field.name === 'is_active')
    : entity.update;
}
export function recordLabel(row: RecordData, entityId?: string): string {
  if (entityId === 'storage-rooms' && typeof row.label === 'string' && row.label) return row.label;
  const identity = entityId ? recordIdentity[entityId] : undefined;
  if (identity)
    return (
      identity
        .map((key) => row[key])
        .filter((value) => value !== null && value !== undefined && value !== '')
        .join(' — ') || 'Record'
    );
  const keys = [
    'product_code',
    'location_code',
    'factory_code',
    'room_code',
    'warehouse_code',
    'bin_code',
    'customer_code',
    'site_code',
    'supplier_code',
    'carrier_code',
    'currency_code',
    'employee_code',
    'document_type_code',
    'term_code',
    'route_code',
    'setting_key',
    'rule_code',
    'warning_code',
    'recorder_model',
    'phase_code',
    'code',
    'code_value',
  ];
  const code = keys.map((key) => row[key]).find((value) => typeof value === 'string' && value);
  const name = Object.entries(row).find(
    ([key, value]) => (key.endsWith('_name') || key === 'display_name') && value
  )?.[1];
  return (
    [code, name].filter(Boolean).join(' — ') ||
    String(row.description ?? row.effective_from ?? 'Record')
  );
}
export function displayValue(row: RecordData, key: string): string {
  if (key === 'room_code' && typeof row.label === 'string' && row.label) return row.label;
  if (isProtectedField(key) && row[key] == null) return 'Restricted';
  if (key.endsWith('_id')) {
    const prefix = key.slice(0, -3);
    const alias = prefix === 'storage_room' ? 'room' : prefix;
    const value = row[`${alias}_code`] ?? row[`${alias}_name`];
    if (value) return String(value);
  }
  if (key === 'authorized_by' && row.authorized_by_employee_code)
    return String(row.authorized_by_employee_code);
  if (['entered_by', 'approved_by', 'authorized_by'].includes(key) && row[`${key}_code`])
    return String(row[`${key}_code`]);
  const value: Value | undefined = row[key];
  if (value === null || value === undefined || value === '') return 'Not recorded';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
export function fillPath(path: string, values: Record<string, Value | undefined>): string {
  return path.replace(/:([a-zA-Z]+)/g, (_, key: string) => {
    const snake = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    const value = values[key] ?? values[snake];
    if (value === undefined || value === null || value === '')
      throw new Error(`Choose ${fieldLabel(snake)} first.`);
    return encodeURIComponent(String(value));
  });
}

/** Avoid presenting a foreign key and its joined business code as two different facts. */
export function detailColumns(entity: EntityContract): string[] {
  const columns =
    entity.id === 'product-prices'
      ? [
          ...entity.columns.filter(
            (key) =>
              ![
                'supplier_code',
                'supplier_name',
                'factory_id',
                'factory_code',
                'factory_name',
              ].includes(key)
          ),
          'source',
        ]
      : entity.id === 'tax-exemptions'
        ? [
            ...entity.columns.filter(
              (key) => !['location_id', 'location_code', 'location_name'].includes(key)
            ),
            'site',
          ]
        : entity.columns;
  return columns.filter(
    (key) =>
      !['id', 'created_at', 'updated_at'].includes(key) &&
      !(entity.id === 'product-prices' && ['entered_by_code', 'approved_by_code'].includes(key)) &&
      !(key.endsWith('_id') && columns.includes(`${key.slice(0, -3)}_code`))
  );
}

export function priceSource(row: RecordData): string {
  const supplier = [row.supplier_code, row.supplier_name].filter(Boolean).join(' — ');
  const factory = row.factory_code || row.factory_name;
  return [supplier, factory].filter(Boolean).join(' / ') || 'All suppliers';
}
export function employeeLocation(row: RecordData): string {
  return String(row.location ?? '') || '—';
}
export function systemUnit(row: RecordData): boolean {
  return ['L', 'BTL', 'BOX', 'PLT'].includes(String(row.code));
}
/** Keep conditional editor state and its submitted payload consistent. */
export function changeRecordField(
  entity: string,
  values: Record<string, string>,
  key: string,
  value: string
): Record<string, string> {
  const next = { ...values, [key]: value };
  if (entity === 'product-prices' && key === 'price_type' && value !== 'BUY') {
    next.supplier_code = '';
    next.factory_id = '';
  }
  if (entity === 'tax-exemptions' && key === 'tax_exempt')
    next.exemption_basis = value === 'true' ? values.exemption_basis || 'certificate' : '';
  return next;
}
export function visibleEditorFields(
  entity: string,
  fields: FieldSpec[],
  values: Record<string, string>
): FieldSpec[] {
  return fields.filter(
    (field) =>
      !(
        entity === 'product-prices' &&
        ['supplier_code', 'factory_id'].includes(field.name) &&
        values.price_type !== 'BUY'
      ) &&
      !(
        entity === 'tax-exemptions' &&
        field.name === 'exemption_basis' &&
        values.tax_exempt !== 'true'
      )
  );
}
